import {
    Box,
    Button,
    ListItemIcon,
    ListItemText,
    MenuItem,
    MenuList,
    Stack,
    Typography,
} from '@mui/material';
import { RotateCcw } from 'lucide-react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { ContourHistoryEntry } from '~/domain/undo';
import { formatOperationLabel } from './operationLabel';

/** A longer structure name is truncated rather than widening the popover. */
const PANEL_MAX_WIDTH = 360;

interface StructureHistoryPanelProps {
    /** The structure the steps belong to; the generic wording when omitted. */
    roiName?: string;
    /** That structure as the structure list draws it, under the heading. */
    roiLabel?: ReactNode;
    /** Retained steps, oldest first — the panel renders them newest first. */
    history: ContourHistoryEntry[];
    discardedStepCount: number;
    canRevertToInitial: boolean;
    /** The whole panel is read-only (approved structure, no writable set). */
    disabled: boolean;
    onSelect: (index: number) => void;
    onRevertToInitial: () => void;
}

/**
 * The selected structure's edit history: every step the undo stack still
 * holds, newest first, with the step the contour currently reflects marked.
 * Picking a step restores it; "Undo all" in the header is the shortcut
 * straight back to the state before the first edit.
 *
 * Two things it must not hide. Steps evicted by the stack's size cap leave a
 * gap, shown in place between the oldest retained edit and the initial state
 * so the list is never read as the complete record. And each step carries the
 * time it was recorded, which is what lets a user match a row against what
 * they remember doing before they commit to jumping to it.
 *
 * Presentational: every decision about what a step means, and whether one can
 * be taken at all, belongs to the container.
 */
export default function StructureHistoryPanel({
    roiName,
    roiLabel,
    history,
    discardedStepCount,
    canRevertToInitial,
    disabled,
    onSelect,
    onRevertToInitial,
}: StructureHistoryPanelProps) {
    const intl = useIntl();

    // A disabled MUI MenuItem is an <li>, not a <button>: MUI marks it
    // aria-disabled and blocks pointer-events in CSS, but leaves the onClick
    // handler attached. Anything that dispatches a click by other means —
    // a keyboard activation, a test, an automation harness — would restore a
    // contour on an approved structure. The guard has to be in the handler.
    const guard = (action: () => void) => () => {
        if (!disabled) {
            action();
        }
    };

    if (history.length === 0) {
        return (
            <Typography
                variant="body2"
                sx={{
                    px: 1,
                    py: 0.5,
                    opacity: 0.85,
                    maxWidth: PANEL_MAX_WIDTH,
                }}
                data-testid="structure-history-empty"
            >
                {roiName
                    ? intl.formatMessage(
                          { id: 'toolbar.history.emptyRoi' },
                          { roiName }
                      )
                    : intl.formatMessage({ id: 'toolbar.history.empty' })}
            </Typography>
        );
    }

    const newestFirst = history.toReversed();

    const renderEntry = (entry: ContourHistoryEntry) => {
        const label = entry.isInitial
            ? intl.formatMessage({ id: 'toolbar.history.initialState' })
            : formatOperationLabel(entry.operation) ||
              intl.formatMessage({ id: 'toolbar.history.unnamedStep' });

        return (
            <MenuItem
                key={entry.index}
                dense
                disabled={disabled}
                selected={entry.isCurrent}
                aria-current={entry.isCurrent ? 'step' : undefined}
                onClick={guard(() => onSelect(entry.index))}
                data-testid={`structure-history-step-${entry.index}`}
            >
                <ListItemIcon sx={{ minWidth: 20 }}>
                    <Box
                        sx={(theme) => ({
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            border: `1px solid ${theme.palette.text.secondary}`,
                            backgroundColor: entry.isCurrent
                                ? theme.palette.primary.main
                                : 'transparent',
                            borderColor: entry.isCurrent
                                ? theme.palette.primary.main
                                : theme.palette.text.secondary,
                        })}
                    />
                </ListItemIcon>
                <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                        fontSize: 13,
                        noWrap: true,
                        fontWeight: entry.isCurrent ? 700 : 400,
                    }}
                />
                <Typography
                    component="span"
                    sx={{ fontSize: 11, opacity: 0.8, pl: 1.5 }}
                    title={intl.formatDate(entry.timestamp, {
                        dateStyle: 'medium',
                        timeStyle: 'medium',
                    })}
                >
                    {intl.formatTime(entry.timestamp, {
                        timeStyle: 'medium',
                    })}
                </Typography>
            </MenuItem>
        );
    };

    // The gap the size cap left sits between the initial state and the oldest
    // retained edit, so it is rendered immediately above the initial state.
    const initial = newestFirst.filter((entry) => entry.isInitial);
    const edits = newestFirst.filter((entry) => !entry.isInitial);

    return (
        <Box sx={{ minWidth: 260, maxWidth: PANEL_MAX_WIDTH }}>
            {/* "Undo all" sits in the header, not beside the initial-state
                row it duplicates: the two do the same thing, and next to each
                other they read as two different destinations. Up here it is
                the shortcut past the list, which is what it is. */}
            <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                sx={{ px: 1.5, pb: 0.75, gap: 2 }}
            >
                <Box id="structure-history-heading" sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
                        {intl.formatMessage({ id: 'toolbar.history.title' })}
                    </Typography>
                    {roiLabel}
                </Box>
                <Button
                    size="small"
                    color="inherit"
                    disabled={disabled || !canRevertToInitial}
                    startIcon={<RotateCcw size={13} />}
                    onClick={guard(() => {
                        if (canRevertToInitial) {
                            onRevertToInitial();
                        }
                    })}
                    data-testid="structure-history-revert-all"
                    sx={{
                        fontSize: 12,
                        textTransform: 'none',
                        py: 0,
                        minWidth: 0,
                        flexShrink: 0,
                    }}
                >
                    {intl.formatMessage({
                        id: 'toolbar.history.revertToInitial',
                    })}
                </Button>
            </Stack>
            <MenuList
                dense
                disablePadding
                aria-labelledby="structure-history-heading"
                sx={{ maxHeight: 320, overflowY: 'auto' }}
            >
                {edits.map(renderEntry)}
                {discardedStepCount > 0 && (
                    <Typography
                        sx={{
                            px: 2,
                            py: 0.5,
                            fontSize: 11,
                            fontStyle: 'italic',
                            opacity: 0.8,
                        }}
                        data-testid="structure-history-discarded"
                    >
                        {intl.formatMessage(
                            { id: 'toolbar.history.discardedSteps' },
                            { count: discardedStepCount }
                        )}
                    </Typography>
                )}
                {initial.map(renderEntry)}
            </MenuList>
        </Box>
    );
}
